import type { Registry as Services } from '@ember/service';
import { capitalCase } from 'change-case';
import {
  CENTRAL_MAPPING_UPLOAD_ERROR_CODES,
  CENTRAL_MAPPING_UPLOAD_ROW_LEVEL_ERROR_CODES,
  type CentralMappingUploadErrorCode,
} from 'lumen/utils/central-mapping-constants';

interface UploadApiErrorDetail {
  code?: string;
  source?: {
    cell?: unknown;
  };
}

interface UploadApiError {
  payload: {
    errors: [UploadApiErrorDetail, ...UploadApiErrorDetail[]];
  };
}

interface ErrorCell {
  column: string;
  row: number;
  value: string | null;
}

function hasErrorPayload(error: unknown): error is UploadApiError {
  const errors = (error as UploadApiError)?.payload?.errors;

  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    typeof errors[0] === 'object' &&
    errors[0] !== null
  );
}

function isKnownErrorCode(
  code: string | undefined,
): code is CentralMappingUploadErrorCode {
  return (
    !!code &&
    Object.entries(CENTRAL_MAPPING_UPLOAD_ERROR_CODES).some(
      ([, value]) => value === code,
    )
  );
}

function isErrorCell(cell: unknown): cell is ErrorCell {
  const candidate = cell as Partial<ErrorCell> | undefined;

  return (
    typeof candidate?.row === 'number' &&
    typeof candidate?.column === 'string' &&
    (candidate?.value === null || typeof candidate?.value === 'string')
  );
}

function getErrorCell(entry: UploadApiErrorDetail): ErrorCell | undefined {
  const { cell } = entry.source ?? {};

  return isErrorCell(cell) ? cell : undefined;
}

// Formats row numbers consistently everywhere, e.g. [3, 5, 9] -> "3, 5 and 9".
export function formatRowList(rows: number[], locale: string): string {
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction',
  }).format(rows.map(String));
}

export interface CentralMappingUploadErrorInfo {
  code: CentralMappingUploadErrorCode | null;
  column?: string;
  rows?: number[];
  value?: string;
}

// Extracts the error code from an upload failure, plus every affected row
// for row-level codes, so the caller can pick the right message variant.
export default function getCentralMappingUploadErrorInfo(
  error: unknown,
  errorTracking: Services['error-tracking'],
): CentralMappingUploadErrorInfo {
  const errors = hasErrorPayload(error) ? error.payload.errors : undefined;
  const code = errors?.[0]?.code;

  if (!isKnownErrorCode(code)) {
    // Log unrecognized codes so a missing translation gets noticed.
    errorTracking.captureException(
      new Error(
        `Central mapping upload received unhandled error code: ${code ?? 'none'}`,
      ),
    );

    return { code: null };
  }

  if (!CENTRAL_MAPPING_UPLOAD_ROW_LEVEL_ERROR_CODES.includes(code)) {
    return { code };
  }

  const cells = (errors ?? [])
    .filter((entry) => entry.code === code)
    .map(getErrorCell)
    .filter((cell): cell is ErrorCell => cell !== undefined);

  const [firstCell] = cells;

  if (!firstCell) {
    return { code };
  }

  // invalid_row_value's copy names a single column, so when the same code
  // spans multiple columns only the first column's rows are grouped together.
  const groupedCells =
    code === CENTRAL_MAPPING_UPLOAD_ERROR_CODES.INVALID_ROW_VALUE
      ? cells.filter((cell) => cell.column === firstCell.column)
      : cells;

  const rows = [...new Set(groupedCells.map((cell) => cell.row))].sort(
    (a, b) => a - b,
  );

  return {
    code,
    rows,
    column: capitalCase(firstCell.column),
    value: firstCell.value ?? undefined,
  };
}
